import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  Button,
  Space,
  message,
  Row,
  Col,
  Select,
  Typography,
} from "antd";
import { TCollection } from "./index";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import {
  CloseOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import useFetch from "../../../hooks/useFetch";
import { GetProjectList, AddEditCollections } from "../../../utils/api.constant";

const { Text } = Typography;

interface CollectionFormProps {
  open: boolean;
  onClose: () => void;
  id?: string | null;
  initialData?: any;
  onSuccess?: (savedData: any, isEdit: boolean) => void;
}

export const CollectionForm: React.FC<CollectionFormProps> = ({
  open,
  onClose,
  id,
  initialData,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { post } = useFetch();
  const [projects, setProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        const response = await post(GetProjectList, {
          data: { pageSize: -1, currentPage: 1 },
        });
        if (response.data) {
          const formatted = response.data.map((item: any) => ({
            value: item.id,
            label: item.name,
          }));
          setProjects(formatted);
        }
      } catch (e) {
        console.error("Failed to load projects", e);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (open) {
      if (id && initialData) {
        form.setFieldsValue({
          name: initialData.name,
          description: initialData.description,
          variables: initialData.variables || [],
          projectId: initialData.projectId,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, id, initialData, form]);

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const payload: any = {
        data: {
          ...values,
          type: 1, // eCollectionType.COLLECTION
          parentId: 0
        }
      };
      if (id) {
        payload.data.id = id;
      }

      const response = await post(AddEditCollections, payload);
      if (response && response.dataResponse?.returnCode !== undefined && response.dataResponse?.returnCode !== 0 && response.dataResponse?.returnCode !== 200) {
        message.error(response.dataResponse.message || response.dataResponse.description || "Failed to save collection");
        return;
      }
      message.success(`Collection ${id ? "updated" : "added"} successfully`);
      const responseData = response?.data || { ...payload.data, id: response?.data?.id || `temp_col_${Date.now()}` };
      if (onSuccess) onSuccess(responseData, !!id);
      onClose();
    } catch (error) {
      message.error("An error occurred while saving the collection");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      placement="right"
      width={450}
      onClose={onClose}
      open={open}
      title={id ? "Edit Collection" : "Add Collection"}
      bodyStyle={{ paddingBottom: 80 }}
      footer={null}
    >
      <FormLayout>
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          requiredMark={false}
        >
          <FormHeader>
            <CloseOutlined
              style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
              onClick={onClose}
            />
          </FormHeader>

          <FormContent>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="projectId"
                  label="Project"
                  rules={[
                    { required: true, message: "Please select a project" },
                  ]}
                >
                  <Select
                    placeholder="Select Project"
                    loading={loadingProjects}
                    allowClear
                    options={projects}
                  ></Select>
                </Form.Item>

                <Form.Item
                  name="name"
                  label="Name of Collection"
                  rules={[
                    { required: true, message: "Please enter collection name" },
                  ]}
                >
                  <Input placeholder="e.g. User APIs" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="description" label="Description">
                  <Input.TextArea
                    rows={4}
                    placeholder="Optional details about this collection"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>
                  Variables
                </div>
                <Form.List name="variables">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space
                          key={key}
                          style={{ display: "flex", marginBottom: 8 }}
                          align="baseline"
                        >
                          <Form.Item
                            {...restField}
                            name={[name, "key"]}
                            rules={[
                              {
                                required: true,
                                message: "Missing variable key",
                              },
                            ]}
                            style={{ margin: 0 }}
                          >
                            <Input placeholder="Key (e.g. base_url)" />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, "value"]}
                            rules={[
                              {
                                required: true,
                                message: "Missing variable value",
                              },
                            ]}
                            style={{ margin: 0 }}
                          >
                            <Input placeholder="Value (e.g. https://api.com)" />
                          </Form.Item>
                          <MinusCircleOutlined
                            onClick={() => remove(name)}
                            style={{ color: "red" }}
                          />
                        </Space>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                        >
                          Add Variable
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Col>
            </Row>
          </FormContent>

          <FormFooter>
            <Button
              type="default"
              disabled={loading}
              onClick={onClose}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" onClick={form.submit} loading={loading}>
              Save
            </Button>
          </FormFooter>
        </Form>
      </FormLayout>
    </Drawer>
  );
};
